/**
 * @file    EnterpriseDataGridSection.tsx
 * @scope   ENTERPRISE
 * @summary "Build your data grid" accordion holding the Enterprise V2 partitions manager.
 * @consumes EnterpriseContext, PartitionsManager (DiyEnterprise domain)
 * @produces JSX section rendered inside the drawer main view
 * @sideEffects Syncs partitions/fields through the EnterpriseContext when the selection changes.
 * @notes Rendered only when the selected chart is an Enterprise V2 chart; the caller owns that guard.
 */
import React, { useState } from 'react';
import Accordion from 'core-ui/accordionDrawer/accordionDrawer';
import PartitionsManager from 'components/DiyWidgetEngine/Subtypes/Enterprise/Views/PartitionsManager/PartitionsManager';
import { FieldsManagerTab } from 'components/DiyWidgetEngine/Subtypes/Enterprise/Views/FieldsManager/FieldsManager';
import { useEnterpriseContext } from 'components/DiyWidgetEngine/Subtypes/Enterprise/State/EnterpriseContext';
import { PartitionModel } from 'models/Enterprise/DataSourceModel';

// ⚡ ENTERPRISE V2 — NEW WORLD. Do not use for legacy charts.

export interface EnterpriseDataGridSectionProps {
  /** Data collections offered as selectable partitions. */
  availableOptions: any;
  /** Focuses a partition and navigates to its FieldsManager. */
  setSelectedPartition: (partition: any) => void;
  setCurrentTab: (tab: any) => void;
}

const EnterpriseDataGridSection: React.FC<EnterpriseDataGridSectionProps> = ({
  availableOptions,
  setSelectedPartition,
  setCurrentTab,
}) => {
  // WHY owned here: the expanded flag is pure UI state nobody else reads. It starts open, as it
  // did when the drawer owned it.
  const [isExpanded, setIsExpanded] = useState(true);
  const { state, syncPartitionsWithFields } = useEnterpriseContext();

  /**
   * Translates the PartitionsManager rows into partition models and pushes them to the context.
   * WHY: rows with no selection are placeholders in the UI and must not reach the context.
   */
  const handleDataSourceChange = (dataSources: any[]) => {
    const convertedPartitions = dataSources
      .filter((ds) => ds.selectedValue !== null)
      .map((ds) => ds.selectedValue as PartitionModel);
    syncPartitionsWithFields(convertedPartitions);
  };

  /** Opens the FieldsManager for the given partition. */
  const openFieldsManager = (partitionOption: any) => {
    setSelectedPartition(partitionOption || null);
    setCurrentTab(FieldsManagerTab);
  };

  return (
    <div className="diy-drawer-section">
      <Accordion title="Build your data grid" isExpanded={isExpanded} onToggle={setIsExpanded}>
        <PartitionsManager
          isLoading={!availableOptions?.length}
          availableOptions={availableOptions}
          selectedValues={state.selectedValues.selectedPartitions}
          onChange={handleDataSourceChange}
          onFieldsManager={openFieldsManager}
          selectedEnterpriseFields={state.selectedValues.selectedEnterpriseFields}
        />
      </Accordion>
    </div>
  );
};

export default React.memo(EnterpriseDataGridSection);
